import React from 'react';
import { ModelTier } from './ChatBoxInput';
import { ChevronDownIcon, ChevronRightIcon } from '../icons/AgentIcons';
import { AgentGroup, AgentPreference, LOCKED_AGENT_IDS } from './agentsModalTypes';
import { countRequiredAgents } from './agentsModalUtils';
import { GroupControls } from './GroupControls';
import { AgentRow } from './AgentRow';

interface AgentGroupSectionProps {
  group: AgentGroup;
  collapsed: boolean;
  enabledMap: Record<string, AgentPreference>;
  onToggleCollapse: (groupId: string) => void;
  onToggleAgent: (agentId: string) => void;
  onSelectAll: (groupId: string, value: boolean) => void;
  onModelChange: (agentId: string, modelTier: ModelTier) => void;
  showModelDropdown: boolean;
  projectAgents?: Record<string, AgentPreference>;
  isTaskContext?: boolean;
}

export const AgentGroupSection: React.FC<AgentGroupSectionProps> = ({
  group,
  collapsed,
  enabledMap,
  onToggleCollapse,
  onToggleAgent,
  onSelectAll,
  onModelChange,
  showModelDropdown,
  projectAgents,
  isTaskContext,
}) => (
  <section className="agents-modal__group">
    <div className="agents-modal__group-header">
      <button
        type="button"
        className="agents-modal__group-toggle"
        onClick={() => onToggleCollapse(group.id)}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronRightIcon size={16} /> : <ChevronDownIcon size={16} />}
      </button>

      <div className="agents-modal__group-copy">
        <h3>{group.title}</h3>
        <p>{group.subtitle}</p>
        <div className="agents-modal__group-badges">
          <span className="agents-modal__group-badge">
            {countRequiredAgents(group.agents)} required
          </span>
          <span className="agents-modal__group-badge agents-modal__group-badge--muted">
            {group.agents.length - countRequiredAgents(group.agents)} configurable
          </span>
        </div>
      </div>

      <GroupControls
        group={group}
        enabledMap={enabledMap}
        onSelectAll={onSelectAll}
        projectAgents={projectAgents}
        isTaskContext={isTaskContext}
      />
    </div>

    {!collapsed && (
      <div className="agents-modal__list">
        {group.agents.map((agent) => {
          const isGloballyDisabled = isTaskContext
            && projectAgents !== undefined
            && projectAgents[agent.id]?.enabled === false;
          const toggleDisabled = LOCKED_AGENT_IDS.has(agent.id) || (isGloballyDisabled ?? false);
          const modelDisabled = !enabledMap[agent.id]?.enabled;

          return (
            <AgentRow
              key={agent.id}
              agent={agent}
              preference={enabledMap[agent.id]}
              toggleDisabled={toggleDisabled}
              globallyDisabled={isGloballyDisabled ?? false}
              modelDisabled={modelDisabled}
              onToggle={onToggleAgent}
              onModelChange={onModelChange}
              showModelDropdown={showModelDropdown}
            />
          );
        })}
      </div>
    )}
  </section>
);
